import React from 'react';
import {
    Grid, TextField, Typography, InputAdornment, Icon, Chip,
    IconButton, FormHelperText, createStyles, makeStyles, Button
} from '@material-ui/core';

import { ClientContext } from '../../ClientContext';
import { ClientErrorContext } from '../../ClientErrorContext';
import { TableEmpty } from '../../../../shared/components/TableEmpty';
import { KeywordModel } from '../../../../Models/KeywordModel';
import { EditAction } from '../../../../shared/enum/EditAction';
import { HomeContext } from '../../../Home/HomeContext';
import { FilterChipsSearchField } from '../../../../shared/components/FilterChipsSearchField';
import KeywordService from '../../../../Services/KeywordService';

const useStyles = makeStyles(() => createStyles({
    keywordChipList: {
        overflow: 'auto',
        overflowX: 'hidden',
        maxHeight: '240px'
    },
}));

export function ClientKeywords() {
    const classes = useStyles();

    const homeContext = React.useContext(HomeContext);
    const clientContext = React.useContext(ClientContext);
    const clientErrorContext = React.useContext(ClientErrorContext);

    const [inputKeyword, setInputKeyword] = React.useState<string>('');
    const [searchKeyword, setSearchKeyword] = React.useState<string>('');
    const [fakeIdCounter, setFakeIdCounter] = React.useState(0);

    const handleDownload = async () => {
        try {
            const response = await KeywordService.keywordServiceDownload(clientContext.client.id);
            if (!response || !response.data) {
                throw new Error('Resposta inválida do servidor.');
            }

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');

            a.href = url;
            a.download = `palavras-chave_${clientContext.client.name}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Erro ao baixar o arquivo:', error);
        }
    };

    const handleAddKeywords = () => {
        const keywords = inputKeyword.split(';');
        const listKeywords = [...clientContext.keywords];

        let keywordAlreadyExists = false;
        let fakeId = fakeIdCounter;

        keywords.forEach(k => {
            const findIndex = listKeywords.findIndex(x => x.description === k);
            if (findIndex < 0 && k.trim() != '') {
                const keyword: KeywordModel = { description: k, id: fakeId, active: true, editAction: EditAction.INSERT };
                fakeId--;
                listKeywords.push(keyword);
            }
            else if (k.trim() == '') {
                keywordAlreadyExists = false;
                homeContext.showWarning('Ops... para adicionar uma palavra-chave o campo não pode ser vazio.');
            }
            else
                keywordAlreadyExists = true;
        });

        setFakeIdCounter(fakeId);

        if (keywordAlreadyExists)
            homeContext.showWarning('Ops.. a palavra-chave que você está tentando adicionar já existe.');

        listKeywords.sort((a, b) => ('' + a.description).localeCompare('' + b.description));

        clientContext.setKeywords(listKeywords);
        setInputKeyword('');
    };

    const handleDeleteKeyword = (keywordId: number) => {
        const listKeywordSelect = [...clientContext.keywords];
        const index = listKeywordSelect.findIndex(k => k.id === keywordId);

        if (listKeywordSelect[index].editAction == EditAction.INSERT)
            listKeywordSelect.splice(index, 1);
        else
            listKeywordSelect[index].editAction = EditAction.DELETE;

        clientContext.setKeywords(listKeywordSelect);
    };

    const getDownloadFile = () => {
        return (
            <Button
                onClick={handleDownload}
                size="large"
                color="primary"
                variant="outlined"
            >
                Exportar Palavras-chave
            </Button>
        );
    };

    const getInputField = () => {
        return (
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    id='search-keyword'
                    value={inputKeyword}
                    variant="outlined"
                    label='Palavras-chave/tags'
                    onKeyPress={(evt: React.KeyboardEvent) => { (evt.key === 'Enter') && handleAddKeywords(); }}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => { setInputKeyword(event.target.value); }}
                    helperText="Aperte enter para adicionar"
                    InputProps={{
                        endAdornment:
                            <InputAdornment position="end">
                                <IconButton className={'material-icons-round'} onClick={handleAddKeywords}>
                                    <Icon>add</Icon>
                                </IconButton>
                            </InputAdornment>
                    }} />
            </Grid>
        );
    };

    const getKeywordList = () => {
        const listFilter = searchKeyword !== '' ?
            clientContext.keywords.filter(keyword =>
                keyword.description.toLowerCase().includes(searchKeyword.toLowerCase())) :
            clientContext.keywords;

        return (
            <Grid container spacing={2} className={classes.keywordChipList}>
                {listFilter && listFilter.map((keyword) => {
                    if ((keyword.editAction != EditAction.DELETE) && keyword.active)
                        return (
                            <Grid item key={keyword.id}>
                                <Chip
                                    key={keyword.id}
                                    label={keyword.description}
                                    deleteIcon={<Icon>close</Icon>}
                                    onDelete={() => { handleDeleteKeyword(keyword.id); }}
                                    onClick={() => { handleDeleteKeyword(keyword.id); }}
                                    color='primary' />
                            </Grid>
                        );
                })}
            </Grid>
        );
    };

    const getDisplayKeywordChipList = () => {
        const activeKeywords = clientContext.keywords.filter(k => k.editAction != EditAction.DELETE && k.active);

        if (activeKeywords.length > 0) {
            return (
                <Grid container direction="column" spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FilterChipsSearchField
                            key="client-keyword-search-field"
                            searchText={searchKeyword}
                            setSearchText={setSearchKeyword} />
                    </Grid>

                    <Grid item xs={12} sm={12}>
                        {getKeywordList()}
                    </Grid>

                </Grid>

            );
        }
        else {
            const error = clientErrorContext.keywordsNotDefinedError && !(clientContext.keywords && activeKeywords.length > 0);

            return (
                <>
                    <TableEmpty
                        displayText='Nenhuma palavra-chave cadastrada'
                        cssProperties={{
                            minHeight: '150px',
                            errorBorder: error
                        }} />
                    {error &&
                        <FormHelperText error>
                            Você precisa inserir pelo menos uma palavra-chave para este cliente.
                        </FormHelperText>
                    }
                </>
            );
        }
    };

    return (
        <Grid
            container
            direction="column"
            spacing={2}>

            <Grid item xs={12}>
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item>
                        <Typography variant="subtitle1">
                            Palavras-chave/tags
                        </Typography>
                    </Grid>
                    <Grid item>
                        {getDownloadFile()}
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={12}>
                {getInputField()}
            </Grid>

            <Grid item xs={12}>
                {getDisplayKeywordChipList()}
            </Grid>
        </Grid>
    );

}